import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import Select from 'react-select';

import { setSelectedVariantAndUpdateRange } from '../../../actions/GenomeActions';
import { setSelectedVariantScoreType } from '../../../actions/DeepSEAActions';
import { isExPecto } from '../../../core/util';
import CategoricalLegend from '../../Legend/CategoricalLegend';
import VariantScatterContainer from './VariantScatterContainer';
import { focusGlowStyle } from '../../ui/sharedStyles';
import Tooltip from '../../ui/Tooltip';
import styles from './VariantScatterPlot.css';
import {
  SEQ_CAT_COLORS,
  SEQ_CAT_NAMES,
  ANALYSES_META_INFO,
} from '../../../settings';

const PLOT_COLOR = {
  default: 'gray',
  selected: '#105CC9',
};
const CHART_WIDTH = 800;
const CHR_SEPARATION = 18;

const plotOptions = [
  {
    label: 'Score Rank',
    value: 'rank',
  },
  {
    label: 'Manhattan',
    value: 'manhattan',
  },
];

const colorOptions = [
  {
    label: 'Chromosome',
    value: 'chr',
  },
  {
    label: 'Sequence class',
    value: 'seqclass',
  },
];

const seqColors = {};
Object.keys(SEQ_CAT_COLORS).forEach(seqCode => {
  seqColors[SEQ_CAT_NAMES[seqCode]] = SEQ_CAT_COLORS[seqCode];
});

// Sort by chromosomes for manhattan plot
function genomeSort(data) {
  const dataCopy = Object.assign([], data);
  dataCopy.sort((a, b) => {
    if (a.chromosome !== b.chromosome) {
      const aChr = a.chromosome.replace('chr', '');
      const bChr = b.chromosome.replace('chr', '');
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(aChr) && !isNaN(bChr)) {
        return parseInt(aChr, 10) < parseInt(bChr, 10) ? -1 : 1;
      }
      // eslint-disable-next-line no-restricted-globals
      if (isNaN(aChr) && isNaN(bChr)) {
        return aChr.toUpperCase() === 'X' ? -1 : 1;
      }
      // eslint-disable-next-line no-restricted-globals
      return !isNaN(aChr) ? -1 : 1;
    }
    return a.position - b.position;
  });
  return dataCopy;
}

// Sort by highest score for rankings
function scoreSort(data, scoreType) {
  const dataCopy = Object.assign([], data);
  dataCopy.sort((a, b) => {
    if (Number.isNaN(b[scoreType])) {
      return -1;
    }
    if (Number.isNaN(a[scoreType])) {
      return 1;
    }
    return b[scoreType] - a[scoreType];
  });
  return dataCopy;
}

// Helper function to get documentation URL based on job
const getDocumentationUrl = job => {
  // Check for ExPecto when model is Beluga
  if (job?.ds_model?.toLowerCase() === 'beluga' && job?.expecto) {
    return ANALYSES_META_INFO.expecto?.helpUrl;
  }

  const modelKey = job?.ds_model?.toLowerCase();
  return ANALYSES_META_INFO[modelKey]?.helpUrl;
};

const VariantScatterPlot = ({ data, scoreOptions }) => {
  const dispatch = useDispatch();
  const chromosome = useSelector(state => state.genome.chromosome);
  const startPosition = useSelector(state => state.genome.startPosition);
  const endPosition = useSelector(state => state.genome.endPosition);
  const selectedVariant = useSelector(state => state.genome.selectedVariant);
  const job = useSelector(state => state.deepsea.selectedJob);
  const [manhattan, setManhattan] = useState(true);
  const [seqClass, setSeqClass] = useState(false);
  const [sortedData, setSortedData] = useState([]);
  const [genomeSortedData, setGenomeSortedData] = useState({});
  const [scoreSortedData, setScoreSortedData] = useState({});
  const [dataRegions, setDataRegions] = useState({});
  const [updatedData, setUpdatedData] = useState([]);

  const selectedScoreIdx = scoreOptions
    ? scoreOptions.findIndex(score => isExPecto(score))
    : null;
  const [selectedScoreOption, setSelectedScoreOption] = useState(
    scoreOptions
      ? scoreOptions[selectedScoreIdx < 0 ? 0 : selectedScoreIdx]
      : null,
  );

  useEffect(() => {
    dispatch(setSelectedVariantScoreType(selectedScoreOption));
  }, [selectedScoreOption]);

  useEffect(() => {
    const scoreName = selectedScoreOption.short_name;
    if (scoreName) {
      if (manhattan && genomeSortedData[scoreName])
        setUpdatedData(genomeSortedData[scoreName]);
      else if (scoreSortedData[scoreName])
        setUpdatedData(scoreSortedData[scoreName]);
    }
  }, [manhattan, genomeSortedData, scoreSortedData, selectedScoreOption]);

  useEffect(() => {
    const rawGenomeSortedData = genomeSort(data);

    // Select initial top variant
    dispatch(setSelectedVariantAndUpdateRange(rawGenomeSortedData[0]));

    const tmpDataRegions = {};
    const tmpGenomeSortedData = {};
    const tmpScoreSortedData = {};

    scoreOptions.forEach(score => {
      const scoreName = score.short_name;
      const newScoreData = [];
      const tmpScoreDataRegions = [];
      let beginRegionPosition = 0;
      let endRegionPosition = 0;
      let regionChr = null;
      let beginChrPosition = 0;
      let endChrPosition = 0;

      // Calculate total distance that all chromosomes span
      let genomicRange = 0;

      rawGenomeSortedData.forEach(variant => {
        // Get the range of positions in each chromosome and add them
        if (regionChr !== variant.chromosome) {
          if (regionChr !== null) {
            genomicRange += endRegionPosition - beginRegionPosition;
          }
          beginRegionPosition = variant.position;
          regionChr = variant.chromosome;
        }
        endRegionPosition = variant.position;
      });
      genomicRange += endRegionPosition - beginRegionPosition;

      // Determine space between each chromosome based on total length
      const chrSeparation = Math.max(
        1,
        parseInt((genomicRange / CHART_WIDTH) * CHR_SEPARATION, 10),
      );

      beginRegionPosition = 0;
      endRegionPosition = 0;
      regionChr = null;
      rawGenomeSortedData.forEach(variant => {
        if (regionChr !== variant.chromosome) {
          // Chromosome transition and not first chr
          if (regionChr !== null) {
            tmpScoreDataRegions.push({
              chromosome: regionChr,
              beginRegionPosition,
              endRegionPosition,
              beginChrPosition,
              endChrPosition,
            });

            beginRegionPosition = endRegionPosition + chrSeparation;
          }

          regionChr = variant.chromosome;
          beginChrPosition = variant.position;
        }

        const x = variant.position - beginChrPosition;
        newScoreData.push({
          ...variant,
          x: beginRegionPosition + x,
          y: variant[scoreName],
          color: PLOT_COLOR.default,
        });

        endRegionPosition = beginRegionPosition + x;
        endChrPosition = variant.position;
      });

      tmpScoreDataRegions.push({
        chromosome: regionChr,
        beginRegionPosition,
        endRegionPosition,
        beginChrPosition,
        endChrPosition,
      });

      tmpGenomeSortedData[scoreName] = newScoreData;
      tmpDataRegions[scoreName] = tmpScoreDataRegions;

      // SCORE SORT BEGIN
      const tmpSingleScoreSortedData = scoreSort(data, scoreName);
      const newSingleScoreSortData = [];
      tmpSingleScoreSortedData.forEach((datum, i) => {
        newSingleScoreSortData.push({
          ...datum,
          x: i + 1,
          y: datum[scoreName],
          color: PLOT_COLOR.default,
        });
      });
      tmpScoreSortedData[scoreName] = newSingleScoreSortData;
      // SCORE SORT END
    });

    setGenomeSortedData(tmpGenomeSortedData);
    setDataRegions(tmpDataRegions);
    setScoreSortedData(tmpScoreSortedData);
  }, [data, scoreOptions]);

  useEffect(() => {
    const tmpUpdatedData = updatedData.slice(0);
    for (let i = 0; i < tmpUpdatedData.length; i += 1) {
      // Reset existing variants and set newly selected ones
      const variant = tmpUpdatedData[i];
      variant.color = PLOT_COLOR.default;
      variant.selected = false;
      variant.inRange = false;

      // Find selected variant
      if (
        selectedVariant.chromosome === variant.chromosome &&
        selectedVariant.ref_allele === variant.ref_allele &&
        selectedVariant.alt_allele === variant.alt_allele &&
        selectedVariant.position === variant.position
      ) {
        variant.selected = true;
      }

      // Find variants within the position range
      if (
        variant.position <= endPosition &&
        variant.position >= startPosition &&
        variant.chromosome === chromosome
      ) {
        variant.inRange = true;
        variant.color = PLOT_COLOR.selected;
      }
    }
    setSortedData(tmpUpdatedData);
  }, [updatedData, chromosome, startPosition, endPosition, selectedVariant]);

  const calculateChrPosition = regionPosition => {
    // Check the positions for axis in the graph
    const scoreName = selectedScoreOption.short_name;
    const scoreDataRegions = dataRegions[scoreName];

    if (manhattan) {
      // Find regions that overlap regionPosition
      const regions = scoreDataRegions.filter(
        dataRegion =>
          regionPosition >= dataRegion.beginRegionPosition &&
          regionPosition <= dataRegion.endRegionPosition,
      );

      let chrPosition = '';
      if (regions.length) {
        const region = regions[0];
        chrPosition = regionPosition + region.beginChrPosition;
        if (region.endRegionPosition - region.beginRegionPosition !== 0) {
          chrPosition =
            ((regionPosition - region.beginRegionPosition) /
              (region.endRegionPosition - region.beginRegionPosition)) *
              (region.endChrPosition - region.beginChrPosition) +
            region.beginChrPosition;
        }

        return `${region.chromosome}:${parseInt(chrPosition, 10)}`;
      }

      // Position is in-between regions
      for (let i = 1; i < scoreDataRegions.length; i += 1) {
        const leftRegion = scoreDataRegions[i - 1];
        const rightRegion = scoreDataRegions[i];
        if (
          regionPosition > leftRegion.endRegionPosition &&
          regionPosition < rightRegion.beginRegionPosition
        ) {
          if (
            regionPosition - leftRegion.endRegionPosition <
            rightRegion.beginRegionPosition - regionPosition
          )
            chrPosition = `${leftRegion.chromosome}:${leftRegion.endChrPosition}`;
          else
            chrPosition = `${rightRegion.chromosome}:${rightRegion.beginChrPosition}`;
        }
      }

      // Position is outside all regions
      if (chrPosition === '') {
        const leftRegion = scoreDataRegions[0];
        const rightRegion = scoreDataRegions[scoreDataRegions.length - 1];
        const leftEdge = leftRegion.beginRegionPosition;

        chrPosition =
          regionPosition < leftEdge
            ? `${leftRegion.chromosome}:${leftRegion.beginChrPosition}`
            : `${rightRegion.chromosome}:${rightRegion.endChrPosition}`;
      }

      return chrPosition;
    } // else
    return parseInt(regionPosition, 10);
  };

  function getVariantScatterContainer() {
    return sortedData.length ? (
      <div
        style={{
          marginTop: '5px',
          width: '100%',
        }}
      >
        <VariantScatterContainer
          dataRegions={dataRegions}
          manhattan={manhattan}
          seqClass={seqClass}
          selectedVariantScoreType={selectedScoreOption}
          sortedData={sortedData}
          tickFormat={calculateChrPosition}
          inputType={job?.input_type}
        />
      </div>
    ) : null;
  }

  const scatterContainer = useMemo(() => getVariantScatterContainer(), [
    sortedData,
    manhattan,
    seqClass, // `selectedScoreOption` is missing since it will trigger an update to `sortedData`
  ]);

  const seqColorSelect = (
    <div
      style={{ display: 'inline-block', textAlign: 'left', marginLeft: '10px' }}
    >
      {/* eslint-disable-next-line jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */}
      <label>Color style</label>
      <Select
        isSearchable={false}
        defaultValue={colorOptions[0]}
        onChange={plot => setSeqClass(plot.value === 'seqclass')}
        options={colorOptions}
        styles={{
          control: (provided, state) => ({
            ...focusGlowStyle.control(provided, state),
            zIndex: 10,
            width: 200,
          }),
          menu: selectStyles => ({
            ...selectStyles,
            width: 200,
          }),
        }}
      />
    </div>
  );

  return (
    <div className={styles.container} style={{ textAlign: 'right' }}>
      <div
        style={{
          display: 'inline-block',
          textAlign: 'left',
          marginRight: '10px',
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */}
        <label>
          {`Score type `}
          <Tooltip
            text="Score type documentation"
            url={`${getDocumentationUrl(job)}#output`}
          />
        </label>
        <Select
          isSearchable={false}
          onChange={scoreOption => setSelectedScoreOption(scoreOption)}
          defaultValue={scoreOptions[0]}
          options={scoreOptions}
          isDisabled={scoreOptions.length <= 1}
          styles={{
            control: (provided, state) => ({
              ...focusGlowStyle.control(provided, state),
              zIndex: 10,
              width: 200,
            }),
            menu: selectStyles => ({
              ...selectStyles,
              width: 200,
            }),
          }}
        />
      </div>
      <div style={{ display: 'inline-block', textAlign: 'left' }}>
        {/* eslint-disable-next-line jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */}
        <label>Plot style</label>
        <Select
          isSearchable={false}
          defaultValue={plotOptions[1]}
          onChange={plot => setManhattan(plot.value === 'manhattan')}
          options={plotOptions}
          styles={{
            control: (provided, state) => ({
              ...focusGlowStyle.control(provided, state),
              zIndex: 10,
              width: 200,
            }),
            menu: selectStyles => ({
              ...selectStyles,
              width: 200,
            }),
          }}
        />
      </div>

      {sortedData.length > 0 && sortedData[0]?.max_seq_class && seqColorSelect}

      {seqClass && (
        <CategoricalLegend width={800} height={35} categories={seqColors} />
      )}

      {scatterContainer}
    </div>
  );
};

VariantScatterPlot.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  scoreOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default withStyles(styles)(VariantScatterPlot);
